<script setup>
import { computed, nextTick, ref } from 'vue';
import { useProfileStore } from '@/js/core/stores/profile-store';

const props = defineProps({
  visibility: {
    type: Object,
    default: () => ({}),
  },
});

const profileStore = useProfileStore();
const visibilityControlId = 'profile-visibility-toggle';
const visibilityDescriptionId = 'profile-visibility-description';

const isProfileVisible = computed(() => Boolean(props.visibility.is_visible));
const isVisibilityForcedPrivate = computed(() => Boolean(props.visibility.forced_private));
const canUpdateProfileVisibility = computed(() => props.visibility.can_update !== false);
const isSavingVisibility = ref(false);
const visibilityError = ref('');
const visibilityFieldError = ref('');

const ferpaHiddenNote =
  'Our records indicate that you have a FERPA privacy restriction on your student record, so your profile is currently hidden on GSD Now. If you choose to make your profile visible, you will be voluntarily removing this FERPA restriction for GSD Now, and other users will be able to view your profile details.';
const ferpaVisibleNote =
  'You have voluntarily chosen to remove your FERPA privacy restriction on GSD Now. Other users will be able to view your profile details.';
const isFerpaBlocked = computed(() => Boolean(props.visibility.ferpa_privacy_block));
const isFerpaOptedIn = computed(() => Boolean(props.visibility.ferpa_opted_in));
const showFerpaConfirmation = ref(false);
const ferpaConfirmButtonRef = ref(null);

const focusVisibilityToggle = () => {
  nextTick(() => document.getElementById(visibilityControlId)?.focus());
};

const saveVisibility = async (nextVisibility, { onError } = {}) => {
  isSavingVisibility.value = true;
  visibilityError.value = '';
  visibilityFieldError.value = '';

  try {
    await profileStore.saveProfileVisibility(nextVisibility);
  } catch (error) {
    onError?.(error);
    visibilityFieldError.value = error.errors?.profile_visibility || '';
    visibilityError.value = visibilityFieldError.value
      ? ''
      : error.message || 'Failed to save your profile visibility. Please try again.';
  } finally {
    isSavingVisibility.value = false;
  }
};

const handleVisibilityChange = async event => {
  if (!canUpdateProfileVisibility.value) {
    return;
  }

  const nextVisibility = event.target.checked;

  // First FERPA opt-in must be confirmed before anything is saved.
  if (isFerpaBlocked.value && nextVisibility === true && !isFerpaOptedIn.value) {
    event.target.checked = false;
    visibilityError.value = '';
    visibilityFieldError.value = '';
    showFerpaConfirmation.value = true;
    nextTick(() => ferpaConfirmButtonRef.value?.focus());
    return;
  }

  await saveVisibility(nextVisibility, {
    onError: () => {
      event.target.checked = isProfileVisible.value;
    },
  });
};

const confirmFerpaOptIn = async () => {
  await saveVisibility(true);
  showFerpaConfirmation.value = false;
  focusVisibilityToggle();
};

const cancelFerpaOptIn = () => {
  showFerpaConfirmation.value = false;
  focusVisibilityToggle();
};
</script>

<template>
  <li class="profile-page_profile-grid-item visibility">
    <label class="profile-page_visibility-toggle-row" :for="visibilityControlId">
      <span class="profile-page_visibility-toggle-label">Profile visibility</span>
      <span class="profile-page_visibility-toggle-control">
        <input
          :id="visibilityControlId"
          type="checkbox"
          class="profile-page_visibility-toggle-input"
          :checked="isProfileVisible"
          :disabled="!canUpdateProfileVisibility || isSavingVisibility"
          :aria-describedby="visibilityDescriptionId"
          :aria-invalid="Boolean(visibilityFieldError)"
          @change="handleVisibilityChange"
        />
        <span class="profile-page_visibility-toggle-background" aria-hidden="true">
          <span
            class="profile-page_visibility-toggle-slider"
            :class="{ 'visibility-on': isProfileVisible }"
          ></span>
        </span>
      </span>
    </label>
    <div :id="visibilityDescriptionId" aria-live="polite">
      <div
        v-if="!isFerpaBlocked && isVisibilityForcedPrivate"
        class="profile-page_visibility-message denied"
      >
        Your profile is private because of school privacy settings. It is not available to other GSD
        Now users.
      </div>
      <div v-else-if="!isProfileVisible" class="profile-page_visibility-message denied">
        Your profile will not be available to other GSD Now users. Consequently, you will not be
        able to create or contribute to any Trays, all Trays you have created and own will become
        private, and all posts you have contributed to any Trays will be hidden.
      </div>
      <div v-else class="profile-page_visibility-message accepted">
        Your profile will be available to all GSD Now users. It can be accessed whenever you create
        or contribute to any Trays.
      </div>
      <div
        v-if="isFerpaBlocked && !isProfileVisible"
        class="profile-page_visibility-message denied ferpa"
      >
        {{ ferpaHiddenNote }}
        <div v-if="showFerpaConfirmation" class="profile-page_ferpa-confirmation-actions">
          <button
            ref="ferpaConfirmButtonRef"
            type="button"
            class="btn-primary"
            :disabled="isSavingVisibility"
            @click="confirmFerpaOptIn"
          >
            Make my profile visible
          </button>
          <button
            type="button"
            class="profile-page_ferpa-cancel-button"
            :disabled="isSavingVisibility"
            @click="cancelFerpaOptIn"
          >
            Cancel
          </button>
        </div>
      </div>
      <div
        v-else-if="isFerpaBlocked && isProfileVisible"
        class="profile-page_visibility-message accepted ferpa"
      >
        {{ ferpaVisibleNote }}
      </div>
    </div>
    <p v-if="visibilityFieldError" class="profile-page_visibility-message denied" role="alert">
      {{ visibilityFieldError }}
    </p>
    <p v-if="visibilityError" class="profile-page_visibility-message denied" role="alert">
      {{ visibilityError }}
    </p>
  </li>
</template>
